import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { stripeApi } from '@/lib/api';
import { useBusinessNav } from '@/hooks/useBusinessNav';

export function StripeRefresh() {
  const { businessId } = useParams<{ businessId: string }>();
  const { nav } = useBusinessNav();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!businessId) {
      setStatus('error');
      setError('Missing business ID. Please try again from Settings.');
      return;
    }

    // Generate a new onboarding link and redirect
    stripeApi.connect(businessId).then((response) => {
      if (response.error) {
        setStatus('error');
        setError(response.error);
        return;
      }

      if (response.data?.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      } else {
        setStatus('error');
        setError('Failed to generate new onboarding link. Please try again from Settings.');
      }
    });
  }, [businessId]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">Unable to Continue Setup</h2>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
              <Button onClick={() => nav('/settings/payments')}>
                Go to Payment Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">Resuming Stripe Setup...</h2>
              <p className="text-sm text-muted-foreground">
                Please wait while we redirect you to Stripe.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
